import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-217 — Per-model system-prompt editor.
//
// Surfaces from the chat header as a slide-down editor. Manages a
// {model_id: prompt} map plus a `default` fallback the runtime uses
// when the active model has no specific entry. Save fires the
// onSave callback which the consumer wires to the runtime's
// agent_set_system_prompt RPC; takes effect on the next turn.
import { useEffect, useMemo, useState } from "react";
export function SystemPromptEditor({ activeModel, initial, onSave, onClose }) {
    // The variant the user is currently editing — defaults to the active
    // model so opening the editor while talking to gpt-5 shows the gpt-5
    // prompt. Falls back to "default" when no per-model entry exists.
    const [editingFor, setEditingFor] = useState(() => initial.perModel[activeModel] !== undefined ? activeModel : "default");
    const [variants, setVariants] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        setVariants(initial);
        setDirty(false);
    }, [initial]);
    const value = useMemo(() => {
        if (editingFor === "default")
            return variants.default;
        return variants.perModel[editingFor] ?? "";
    }, [editingFor, variants]);
    const setValue = (next) => {
        setDirty(true);
        if (editingFor === "default") {
            setVariants((v) => ({ ...v, default: next }));
        }
        else {
            setVariants((v) => ({ ...v, perModel: { ...v.perModel, [editingFor]: next } }));
        }
    };
    const removeVariant = (modelId) => {
        setDirty(true);
        setVariants((v) => {
            const { [modelId]: _drop, ...rest } = v.perModel;
            return { ...v, perModel: rest };
        });
        if (editingFor === modelId)
            setEditingFor("default");
    };
    const addVariantForActive = () => {
        if (variants.perModel[activeModel] !== undefined) {
            setEditingFor(activeModel);
            return;
        }
        setDirty(true);
        setVariants((v) => ({ ...v, perModel: { ...v.perModel, [activeModel]: v.default } }));
        setEditingFor(activeModel);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(variants);
            setDirty(false);
        }
        finally {
            setSaving(false);
        }
    };
    const variantOptions = ["default", ...Object.keys(variants.perModel)];
    return (_jsxs("div", { style: {
            background: "var(--paper)",
            borderBottom: "1px solid var(--hairline)",
            padding: "10px 14px",
            fontFamily: "var(--sans)",
            fontSize: "var(--fs-body)",
            color: "var(--ink)",
        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }, children: [_jsx("span", { style: { fontFamily: "var(--serif)", fontSize: 14, fontWeight: 500 }, children: "System prompt" }), _jsx("select", { value: editingFor, onChange: (e) => setEditingFor(e.target.value), style: {
                            font: "inherit",
                            padding: "2px 8px",
                            borderRadius: "var(--radius-sm)",
                            background: "var(--paper-2)",
                            color: "var(--ink-2)",
                            border: "1px solid var(--hairline)",
                        }, children: variantOptions.map((id) => (_jsx("option", { value: id, children: id === "default" ? "default (fallback)" : id }, id))) }), variants.perModel[activeModel] === undefined ? (_jsxs("button", { type: "button", onClick: addVariantForActive, style: btnStyle(), title: `Add a variant for ${activeModel}`, children: ["+ variant for ", activeModel] })) : (editingFor !== "default" && (_jsx("button", { type: "button", onClick: () => removeVariant(editingFor), style: btnStyle("danger"), title: "Remove this variant; the agent falls back to default", children: "remove variant" }))), _jsx("span", { style: { flex: 1 } }), _jsxs("span", { style: { fontSize: "var(--fs-meta)", color: "var(--ink-3)" }, children: ["active model: ", _jsx("code", { children: activeModel })] })] }), _jsx("textarea", { value: value, onChange: (e) => setValue(e.target.value), rows: 8, style: {
                    width: "100%",
                    padding: 10,
                    fontFamily: "var(--mono)",
                    fontSize: "var(--fs-code)",
                    background: "var(--paper-2)",
                    color: "var(--ink)",
                    border: "1px solid var(--hairline)",
                    borderRadius: "var(--radius-sm)",
                    resize: "vertical",
                }, placeholder: "System prompt for this variant\u2026" }), _jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 8,
                    marginTop: 8,
                }, children: [_jsx("span", { style: { flex: 1, fontSize: "var(--fs-meta)", color: "var(--ink-3)" }, children: "Takes effect on the next turn \u2014 the runtime re-reads the config between turns." }), _jsx("button", { type: "button", onClick: onClose, style: btnStyle(), children: "close" }), _jsx("button", { type: "button", onClick: handleSave, disabled: !dirty || saving, style: btnStyle("primary", !dirty || saving), children: saving ? "saving…" : "save" })] })] }));
}
function btnStyle(variant = "default", disabled = false) {
    const base = {
        font: "inherit",
        fontSize: "var(--fs-meta)",
        padding: "4px 10px",
        borderRadius: 999,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink-2)",
    };
    if (variant === "primary") {
        base.background = "var(--accent)";
        base.color = "var(--paper)";
        base.borderColor = "var(--accent)";
    }
    else if (variant === "danger") {
        base.color = "var(--accent-ink)";
        base.borderColor = "color-mix(in oklab, var(--accent) 40%, transparent)";
    }
    return base;
}
//# sourceMappingURL=SystemPromptEditor.js.map